import { useContext } from 'react';
import { Flex, Typography } from 'antd';
import { ErrorMessageContext } from '../../../../context/ErrorMessageContext';
import { API } from '../../../../services';
import { useTableManager } from '../../../../hooks/logs/useTableManager';
import { useDownloadManager } from '../../../../hooks/logs/useDownloadManager';
import {
  ANOMALY_ALERTS_FILTER_KEYS,
  ANOMALY_TYPE,
  anomalyDownloadFormatOptions,
  HYPHEN,
} from '../../../../utilities/constants';
import { getAnomalyLogsColumns } from '../columnConfig';
import LogsLayout from '../LogsLayout';
import {
  generateTimestampedFilename,
  snakeToTitleCase,
} from '../../../../utilities/commonFunctions';
import Icon from '@ant-design/icons';
import { ReactComponent as MajorAnomalySeverity } from '../../../../assets/icons/severity-major-anomaly.svg';
import { ReactComponent as MinorAnomalySeverity } from '../../../../assets/icons/severity-minor-anomaly.svg';
import { ReactComponent as SOSAnomalySeverity } from '../../../../assets/icons/severity-sos-anomaly.svg';

const { Text } = Typography;

const AnomalyLogs = () => {
  const { addErrorMessage } = useContext(ErrorMessageContext);

  const {
    logsData,
    setLogsData,
    loading,
    tableParams,
    setTableParams,
    fetchData,
  } = useTableManager(API.getAnomalyReports);

  const { downloadLoading, handleDownload } = useDownloadManager(
    API.getAnomalyLogsDownload,
    generateTimestampedFilename('anomaly_logs'),
    addErrorMessage,
    anomalyDownloadFormatOptions
  );

  const formatData = response => {
    return response?.results?.map((log, index) => ({
      key:
        tableParams?.pagination?.current * tableParams?.pagination?.pageSize +
        index,
      trip_id: log?.trip_id || HYPHEN,
      anomaly_type: log?.anomaly_type || HYPHEN,
      severity: log?.severity || HYPHEN,
      timestamp: log?.timestamp || HYPHEN,
      driver_name: log?.driver_name || HYPHEN,
      total_passengers: log?.total_passengers || HYPHEN,
      map: {
        latitude: log?.latitude || HYPHEN,
        longitude: log?.longitude || HYPHEN,
      },
      ridebuddy_deviceid: log?.ridebuddy_deviceid,
      anomaly_id: log?.anomaly_id,
      device_id: log?.device_id || HYPHEN,
      vehicle_number: log?.vehicle_number || HYPHEN,
      sticker_id: log?.sticker_id || HYPHEN,
    }));
  };

  const getSeverityText = severity => {
    let labelText = 'Anomaly';
    let labelIcon = MajorAnomalySeverity;
    if (severity?.toLowerCase()?.includes('critical')) {
      labelText = 'SOS';
      labelIcon = SOSAnomalySeverity;
    } else if (severity?.toLowerCase()?.includes('major')) {
      labelText = 'Major Anomaly';
      labelIcon = MajorAnomalySeverity;
    } else if (severity?.toLowerCase()?.includes('minor')) {
      labelText = 'Minor Anomaly';
      labelIcon = MinorAnomalySeverity;
    }

    return (
      <Flex gap={8}>
        <Icon component={labelIcon} className="severity-icon" />
        <Text>{labelText}</Text>
      </Flex>
    );
  };

  const getSeverityColumn = () => ({
    title: 'Severity',
    dataIndex: 'severity',
    key: 'severity',
    ellipsis: true,
    sort: true,
    selectFilter: true,
    selectFilterOptions: Object.entries(ANOMALY_TYPE)
      ?.filter(
        ([key]) => key !== 'DRIVER_DROWSINESS' && key !== 'DRIVER_ANOMALIES'
      )
      ?.map(([key, value]) => ({
        label: value,
        value: key,
      })),
    render: severity => <Text>{getSeverityText(severity)}</Text>,
  });

  const getAnomalyColumn = () => {
    return {
      title: 'Anomaly Type',
      dataIndex: 'anomaly_type',
      key: 'anomaly_type',
      ellipsis: true,
      sort: true,
      selectFilter: true,
      selectFilterOptions: Object.entries(ANOMALY_ALERTS_FILTER_KEYS)?.map(
        ([key, value]) => ({
          label: snakeToTitleCase(key),
          value: value,
        })
      ),
      render: anomalyType => <Text>{snakeToTitleCase(anomalyType)}</Text>,
    };
  };

  const anomalyColumns = getAnomalyLogsColumns();
  const columns = [
    ...anomalyColumns.slice(0, 4),
    getAnomalyColumn(),
    getSeverityColumn(),
    ...anomalyColumns.slice(4),
  ];

  return (
    <>
      <LogsLayout
        logsData={logsData}
        setLogsData={setLogsData}
        loading={loading}
        tableParams={tableParams}
        setTableParams={setTableParams}
        fetchData={fetchData}
        formatData={formatData}
        columns={columns}
        downloadLoading={downloadLoading}
        handleDownload={handleDownload}
      />
    </>
  );
};

export default AnomalyLogs;
